import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { Box, Stack, TextField, TextFieldProps } from '@mui/material';
import {
  IntegrationChannelViewFragment,
  IntegrationDirection,
  IntegrationInputData,
  IntegrationType,
  UpdateIntegrationAuthData,
} from 'generated/graphql';
import { ChangeEvent, useState } from 'react';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

interface SingleInputConnectIntegrationFormDrawerContentProps {
  integrationChannel: IntegrationChannelViewFragment;
  integrationType: IntegrationType;
  inputFieldData: string;
  setInputFieldData: (value: string) => void;
  integrationData: IntegrationInputData;
  updateAuthData?: UpdateIntegrationAuthData;
  textFieldProps?: TextFieldProps;
  onDrawerClose: () => void;
  integrationID?: string;
}

export const SingleInputConnectIntegrationFormDrawerContent = ({
  integrationChannel,
  inputFieldData,
  setInputFieldData,
  integrationData,
  updateAuthData,
  integrationType,
  textFieldProps = {},
  onDrawerClose,
  integrationID,
}: SingleInputConnectIntegrationFormDrawerContentProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const [descriptionInput, setDescriptionInput] = useState('');

  const { createIntegration, status: createIntegrationStatus } =
    useCreateIntegrationEdgeServer({
      tenantID,
      direction: IntegrationDirection.Inbound,
      channel: integrationChannel.type,
      integrationType,
      createdByUser: userID,
      inputData: integrationData,
      description: descriptionInput.trim(),
      onCompleted: () => {
        onDrawerClose();
        trackIntegrationEvent({
          eventName: 'Integration Added',
          integrationChannelType: integrationChannel.type,
        });
      },
    });

  const isReauthorizeForm = !!integrationID;

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      action: 'reauth',
      channelType: integrationChannel.type,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
      },
    });

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    shouldValidateDescription: !isReauthorizeForm,
    channelType: integrationChannel.type,
    integrationType,
    hasValidFormFields: !!inputFieldData,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      if (integrationID) {
        await updateIntegration({
          integrationID,
          isActive: true,
          updateAuthData: updateAuthData,
        });
      } else {
        await createIntegration();
      }
      trackIntegrationEvent({
        eventName: 'Integration Form Connect CTA Clicked',
        integrationChannelType: integrationChannel.type,
      });
    },
  });

  const isLoading =
    IsLoading(createIntegrationStatus) || IsLoading(updateIntegrationStatus);

  const handleTextInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputFieldData(event.target.value);
  };

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
          isReauthorizeForm={isReauthorizeForm}
        />
        <Stack p={3} spacing={4}>
          <TextField
            {...textFieldProps}
            fullWidth
            variant="outlined"
            onChange={handleTextInputChange}
            disabled={isLoading}
            required
          />
          {!isReauthorizeForm && (
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          )}
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText={isReauthorizeForm ? 'Reauthorize' : 'Integrate'}
        isDisabled={isDisabled}
        isLoading={isLoading}
      />
    </Stack>
  );
};
